<template>
  <div
    ref="modalRef"
    class="modal pinning-session-modal"
    role="dialog"
    data-testid="pinning-session-modal"
  >
    <div class="pinning-session-modal__dialog modal-dialog">
      <!-- Loading pins overlay -->
      <div
        v-if="isLoadingPins"
        class="absolute inset-0 z-50 flex items-center justify-center bg-white/70"
        data-testid="artboard-loading-overlay"
      >
        <div class="flex flex-col items-center gap-2">
          <UseSvg icon="loader" class="h-8 w-8 animate-spin text-blue-700" />
          <span class="text-sm font-medium text-gray-700">Loading pins...</span>
        </div>
      </div>

      <!-- Mobile layout -->
      <template v-if="!panel.isDesktop.value">
        <PinningHeader :is-desktop="false" />

        <div class="flex min-h-0 flex-1 flex-col-reverse">
          <!-- Panel (bottom, expands upward via flex-col-reverse) -->
          <div
            :class="[
              'mx-2 mb-1 shrink-0 overflow-hidden rounded-xl bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] transition-all duration-300 ease-out',
              panel.isPanelExpanded.value ? 'mt-1 max-h-screen min-h-0 flex-1' : 'max-h-[40vh]'
            ]"
          >
            <PinningPanel :is-desktop="false" />
          </div>

          <!-- Artboard (above panel due to flex-col-reverse) -->
          <div
            :class="[
              'relative flex-1 overflow-hidden transition-all duration-300 ease-out',
              panel.isPanelExpanded.value ? 'max-h-0' : 'max-h-screen'
            ]"
          >
            <PinningArtboard
              :image-url="artworkImageUrl"
              :pins="panel.pins.value"
              :active-pin-id="panel.activePinId.value"
              :hovered-pin-id="panel.hoveredPinId.value"
              :show-pins="panel.showPins.value"
              :can-add-pin="panel.canAddNewPin.value || panel.canRepositionActivePin.value"
              :zoom="zoom"
              :show-tooltip="showTooltip"
              :tooltip-message="tooltipMessage"
              @add-pin="handleAddPin"
              @pin-click="handlePinClick"
              @pin-hover="
                id => {
                  panel.hoveredPinId.value = id;
                  panel.hoveredPinSource.value = 'artboard';
                }
              "
              @pin-unhover="
                panel.hoveredPinId.value = null;
                panel.hoveredPinSource.value = null;
              "
              @tooltip-dismiss="dismissTooltip"
            />
          </div>
        </div>

        <!-- Mobile toolbar -->
        <div class="flex shrink-0 items-center justify-center gap-3 py-1">
          <div class="flex h-10 items-center rounded-lg bg-white px-3 shadow-md">
            <CheckBox label="Show pins" :style="'gray'" data-testid="show-pins-toggle">
              <input
                type="checkbox"
                :checked="panel.showPins.value"
                @change="panel.showPins.value = $event.target.checked"
              />
            </CheckBox>
          </div>
          <PinningZoom :zoom="zoom" :is-desktop="false" @update:zoom="val => (zoom = val)" />
        </div>

        <PinningFeedback
          v-if="isUserSignedIn"
          :positive="feedback.positive.value"
          :message="feedback.message.value"
          :is-open="feedback.isOpen.value"
          :status="feedback.status.value"
          :is-desktop="false"
          @select-positive="feedback.handleSelectPositive"
          @select-negative="feedback.handleSelectNegative"
          @cancel="feedback.handleCancel"
          @submit="feedback.handleSubmit"
          @update:message="
            val => {
              feedback.message.value = val;
            }
          "
        />

        <PinningActionBar
          variant="static"
          :is-desktop="false"
          :is-cancel-disabled="isCompleting || isCancelling"
          :is-done-disabled="
            isCompleting ||
            !panel.hasCompletedPin.value ||
            isCancelling ||
            panel.hasAiAssistanceOngoing.value
          "
          :is-loading="isCompleting"
          @cancel="handleCancel"
          @done="handleDone"
          @done-disabled-click="panel.hasUnfinishedPin.value && (showUnfinishedModal = true)"
          @escape-hatch-click="escapeHatch.handleEscapeHatchClick"
        />
      </template>

      <!-- Desktop layout -->
      <template v-else>
        <div class="flex h-full flex-1 gap-3 overflow-hidden">
          <div
            class="flex w-2/5 min-w-[400px] max-w-[700px] flex-col overflow-hidden border border-gray-200 bg-white shadow"
          >
            <PinningPanel :is-desktop="true" />
          </div>

          <div class="relative flex-1 overflow-hidden">
            <PinningArtboard
              :is-desktop="true"
              :is-feedback-visible="isUserSignedIn"
              :image-url="artworkImageUrl"
              :pins="panel.pins.value"
              :active-pin-id="panel.activePinId.value"
              :hovered-pin-id="panel.hoveredPinId.value"
              :show-pins="panel.showPins.value"
              :can-add-pin="panel.canAddNewPin.value || panel.canRepositionActivePin.value"
              :zoom="zoom"
              :show-tooltip="showTooltip"
              :tooltip-message="tooltipMessage"
              @add-pin="handleAddPin"
              @pin-click="handlePinClick"
              @pin-hover="
                id => {
                  panel.hoveredPinId.value = id;
                  panel.hoveredPinSource.value = 'artboard';
                }
              "
              @pin-unhover="
                panel.hoveredPinId.value = null;
                panel.hoveredPinSource.value = null;
              "
              @tooltip-dismiss="dismissTooltip"
            />

            <div class="absolute right-2 top-2 z-10 flex items-center gap-2">
              <div class="flex h-10 items-center rounded-lg bg-white px-3 shadow-md">
                <CheckBox label="Show pins" :style="'gray'" data-testid="show-pins-toggle">
                  <input
                    type="checkbox"
                    :checked="panel.showPins.value"
                    @change="panel.showPins.value = $event.target.checked"
                  />
                </CheckBox>
              </div>
              <PinningZoom :zoom="zoom" :is-desktop="true" @update:zoom="val => (zoom = val)" />
            </div>

            <PinningFeedback
              v-if="isUserSignedIn"
              :positive="feedback.positive.value"
              :message="feedback.message.value"
              :is-open="feedback.isOpen.value"
              :status="feedback.status.value"
              :is-desktop="true"
              @select-positive="feedback.handleSelectPositive"
              @select-negative="feedback.handleSelectNegative"
              @cancel="feedback.handleCancel"
              @submit="feedback.handleSubmit"
              @update:message="
                val => {
                  feedback.message.value = val;
                }
              "
            />

            <PinningActionBar
              :is-desktop="true"
              :is-cancel-disabled="isCompleting || isCancelling"
              :is-done-disabled="
                isCompleting ||
                !panel.hasCompletedPin.value ||
                isCancelling ||
                panel.hasAiAssistanceOngoing.value
              "
              :is-loading="isCompleting"
              @cancel="handleCancel"
              @done="handleDone"
              @done-disabled-click="panel.hasUnfinishedPin.value && (showUnfinishedModal = true)"
              @escape-hatch-click="escapeHatch.handleEscapeHatchClick"
            />
          </div>
        </div>
      </template>
    </div>

    <!-- Inline confirmation overlays (not CommonModal, to avoid stacking Bootstrap modals) -->

    <!-- Confirm cancel -->
    <div
      v-if="cancelModalType !== null"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      @click.self="handleStayOnCancelModal"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">
            {{ isNewSessionCancel ? "Are you sure?" : "Remove new pins?" }}
          </h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <p class="m-0 mb-4 text-base font-medium text-gray-700">
            {{
              isNewSessionCancel
                ? "If you exit, all the pins and changes will be deleted."
                : `This will remove ${newPinIds.length} pin${newPinIds.length === 1 ? "" : "s"}, are you sure?`
            }}
          </p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              :disabled="isCancelling"
              data-testid="pinning-confirm-cancel"
              @click="confirmCancel"
            >
              <template v-if="isCancelling">
                {{ isNewSessionCancel ? "Cancelling..." : "Removing pins..." }}
              </template>
              <template v-else>
                {{
                  isNewSessionCancel
                    ? "Yes, Continue"
                    : `Remove pin${newPinIds.length === 1 ? "" : "s"}?`
                }}
              </template>
            </button>
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              style="color: #414192"
              @click="handleStayOnCancelModal"
            >
              {{ isNewSessionCancel ? "No" : "Stay on page" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirm delete pin -->
    <div
      v-if="panel.pendingDeletePinId.value"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      @click.self="panel.closeDeletePinModal"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">Delete pin?</h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <p class="m-0 mb-4 text-base font-medium text-gray-700">
            Are you sure you want to delete the pin?
          </p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#c90505] bg-[#c90505] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              @click="panel.confirmDeletePin"
            >
              Delete
            </button>
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              style="color: #414192"
              @click="panel.closeDeletePinModal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit pin instruction -->
    <div
      v-if="panel.editingPinId.value"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      @click.self="panel.closeEditPinModal"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">
            Edit Pin {{ editingPinNumber }} Instructions
          </h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <textarea
            v-model="editInstructionText"
            style="font-size: 16px"
            class="mp-unmask font-inherit mb-4 w-full resize-none rounded-lg border border-gray-300 p-2.5 outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
            rows="4"
            data-testid="edit-pin-instruction-textarea"
          />
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              :disabled="panel.isUpdatingPinInstruction.value"
              @click="panel.handleUpdatePinInstruction(editInstructionText)"
            >
              {{ panel.isUpdatingPinInstruction.value ? "Saving..." : "Save" }}
            </button>
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              style="color: #414192"
              @click="panel.closeEditPinModal"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Unfinished pin alert -->
    <div
      v-if="showUnfinishedModal"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      @click.self="showUnfinishedModal = false"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">Unfinished Pin</h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <p class="m-0 mb-4 text-base font-medium text-gray-700">You have an unfinished pin.</p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              @click="showUnfinishedModal = false"
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Escape hatch confirmation -->
    <div
      v-if="escapeHatch.escapeHatchModalOpen.value"
      class="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50"
      @click.self="escapeHatch.handleEscapeHatchDismiss"
    >
      <div class="w-full max-w-sm rounded-lg bg-white p-0 shadow-xl" @click.stop>
        <div class="border-b border-gray-200 px-6 py-4">
          <h3 class="m-0 text-base font-semibold text-gray-700">Use classic edit form?</h3>
        </div>
        <div class="px-6 pb-6 pt-4 text-center">
          <p class="m-0 mb-4 text-base font-medium text-gray-700">
            This will close the pin editor and let you describe your edits in a text form. Your
            placed pins will not be saved. Continue?
          </p>
          <div class="flex flex-col gap-2">
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-base font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              :disabled="isCancelling"
              data-testid="escape-hatch-confirm"
              @click="escapeHatch.handleEscapeHatchConfirm"
            >
              {{ isCancelling ? "Switching..." : "Continue" }}
            </button>
            <button
              type="button"
              class="w-full cursor-pointer rounded-[8px] border border-[#414192] bg-white px-3.5 py-2 text-base font-semibold [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
              style="color: #414192"
              data-testid="escape-hatch-dismiss"
              @click="escapeHatch.handleEscapeHatchDismiss"
            >
              Stay here
            </button>
          </div>
        </div>
      </div>
    </div>

    <PinningToast :toasts="toast.toasts.value" @dismiss="toast.dismiss" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, provide, ref, watch } from "vue";

import UseSvg from "common/vue/components/UseSvg.vue";
import CheckBox from "components/jiffy/check-box/CheckBox.vue";
import { useDtfStore } from "../store";
import { useEscapeHatch } from "./composables/useEscapeHatch";
import { usePinningFeedback } from "./composables/usePinningFeedback";
import { usePinningPanel } from "./composables/usePinningPanel";
import {
  bulkDeletePins,
  cancelSession,
  completeSession,
  fetchPins,
  fetchSessionUpdated
} from "./composables/usePinningSession";
import { usePinningToast } from "./composables/usePinningToast";
import PinningActionBar from "./PinningActionBar.vue";
import PinningArtboard from "./PinningArtboard.vue";
import PinningFeedback from "./PinningFeedback.vue";
import PinningHeader from "./PinningHeader.vue";
import PinningPanel from "./PinningPanel.vue";
import PinningToast from "./PinningToast.vue";
import PinningZoom from "./PinningZoom.vue";
import {
  trackPinsSessionCancellationAborted,
  trackPinsSessionCancelled,
  trackPinsSessionEditCancelFailed,
  trackPinsSessionEditCancelModalConfirmed,
  trackPinsSessionEditCancelModalStayed,
  trackTransfersPinningModeEntered,
  trackTransfersPinningSessionCompleted,
  trackTransfersPinningSessionCompletionFailed
} from "./transfersPinningAnalytics";

defineOptions({ name: "PinningSessionModal" });

const PINNING_TOOLTIP_DISMISSED_KEY = "transfers-pinning-tooltip-dismissed";

const props = defineProps({
  artworkIndex: { type: Number, required: true },
  artworkImageUrl: { type: String, required: true },
  artworkId: { type: [String, Number], required: true },
  sessionId: { type: String, required: true }
});

const store = useDtfStore();

const modalRef = ref(null);
const isCompleting = ref(false);
const isCancelling = ref(false);
const cancelModalType = ref(null); // null | "newSession" | "editSession"
const showUnfinishedModal = ref(false);
const tooltipDismissed = ref(localStorage.getItem(PINNING_TOOLTIP_DISMISSED_KEY) === "true");
const pendingInitialMessage = computed({
  get: () => store.pendingInitialMessage,
  set: val => {
    store.pendingInitialMessage = val;
  }
});
const tooltipMode = ref(tooltipDismissed.value || pendingInitialMessage.value ? null : "initial");
const zoom = ref(1);

const sessionIdRef = computed(() => props.sessionId);
const artworkIdRef = computed(() => String(props.artworkId));

const shouldHydrateExistingSession = computed(() => {
  const artwork = store.artworks[props.artworkIndex];
  return artwork?.pinningSessionId === props.sessionId;
});
const isLoadingPins = ref(false);
const currentUpdatedAt = ref(null);
let pollingInterval = null;

const toast = usePinningToast();
const panel = usePinningPanel(sessionIdRef, artworkIdRef, { toast, pendingInitialMessage });
provide("pinningPanel", panel);
provide("pendingInitialMessage", pendingInitialMessage);
const feedback = usePinningFeedback(sessionIdRef, panel.pins, panel.lastEditedPinId, { toast });

const escapeHatch = useEscapeHatch(panel, {
  async onConfirm() {
    const sessionId = props.sessionId;
    isCancelling.value = true;
    try {
      await cancelSession(sessionId);
    } catch {
      // Proceed even if the API call fails — the escape hatch must remain reliable.
    } finally {
      clearPinningSessionFromStore();
      store.setPinBasedRedrawEscaped(true);
      isCancelling.value = false;
      hideBootstrapModal();
    }
  }
});

const newPins = computed(() => panel.pins.value.filter(p => p.isNew));
const newPinIds = computed(() => newPins.value.map(p => p.id));
const hasNewPins = computed(() => newPins.value.length > 0);
const isNewSessionCancel = computed(() => cancelModalType.value === "newSession");
const isUserSignedIn = computed(() => store.userType === "signed_in");

const editInstructionText = ref("");

const editingPinNumber = computed(() => {
  if (!panel.editingPin.value) return 0;
  return panel.pins.value.findIndex(p => p.id === panel.editingPin.value.id) + 1;
});

const showTooltip = computed(() => tooltipMode.value !== null && !isLoadingPins.value);
const tooltipMessage = computed(() =>
  tooltipMode.value === "initial"
    ? "Click anywhere on the image to drop a pin and add instructions for our artists."
    : "Click anywhere on the image to add another pin"
);

// Tooltip management
watch(
  [
    () => panel.pins.value.length,
    () => panel.hasCompletedPin.value,
    () => panel.canAddNewPin.value,
    () => panel.hasUnfinishedPin.value
  ],
  () => {
    if (tooltipDismissed.value || pendingInitialMessage.value) {
      if (tooltipMode.value !== null) tooltipMode.value = null;
      return;
    }

    if (panel.pins.value.length > 0 && tooltipMode.value === "initial") {
      tooltipMode.value = null;
      return;
    }

    if (panel.hasUnfinishedPin.value) {
      tooltipMode.value = null;
      return;
    }

    if (!panel.hasCompletedPin.value || !panel.canAddNewPin.value) {
      if (tooltipMode.value === "add-next") {
        tooltipMode.value = null;
      }
      return;
    }

    tooltipMode.value = "add-next";
  }
);

// Populate edit instruction text when editing modal opens
watch(
  () => panel.editingPin.value,
  newVal => {
    if (newVal) {
      editInstructionText.value = newVal.instruction || "";
    }
  }
);

const PINNING_MODAL_Z_INDEX = 2147483000;
const PINNING_MODAL_BACKDROP_Z_INDEX = 2147482990;

function applyModalStackingOrder() {
  const modalElement = modalRef.value;
  if (modalElement) {
    modalElement.style.zIndex = String(PINNING_MODAL_Z_INDEX);
  }

  // Bootstrap appends backdrop to body; adjust the latest one for this modal.
  const latestBackdrop = document.querySelector(".modal-backdrop:last-of-type");
  if (latestBackdrop instanceof HTMLElement) {
    latestBackdrop.style.zIndex = String(PINNING_MODAL_BACKDROP_Z_INDEX);
  }
}

// Bootstrap modal show/hide
function showBootstrapModal() {
  const $modal = $(modalRef.value);
  $modal
    .modal({ backdrop: "static", keyboard: false })
    .modal("show")
    .on("shown.bs.modal", () => {
      applyModalStackingOrder();
    })
    .on("hidden.bs.modal", () => {
      $modal.off("shown.bs.modal");
      $modal.off("hidden.bs.modal");
      store.closePinningSession();
    });
}

function hideBootstrapModal() {
  $(modalRef.value).modal("hide");
}

async function hydrateExistingSession() {
  if (!shouldHydrateExistingSession.value) return;

  isLoadingPins.value = true;
  try {
    const pinsData = await fetchPins(props.sessionId);
    panel.hydratePins(pinsData);
    tooltipMode.value = null;
    // Get initial updated_at from the response if available
    if (pinsData.updatedAt !== undefined) {
      currentUpdatedAt.value = pinsData.updatedAt;
    } else {
      // Fallback: fetch it separately if not in response
      try {
        const updatedData = await fetchSessionUpdated(props.sessionId);
        currentUpdatedAt.value = updatedData.updatedAt;
      } catch {
        // Ignore errors on initial fetch
      }
    }
  } catch {
    toast.error({
      title: "Failed to load pins",
      description: "Something went wrong. Please try again."
    });
  } finally {
    isLoadingPins.value = false;
  }
}

function setupSessionPolling() {
  const POLLING_INTERVAL_MS = 12000;
  pollingInterval = setInterval(async () => {
    if (!props.sessionId) return;
    try {
      const updatedData = await fetchSessionUpdated(props.sessionId);
      const newUpdatedAt = updatedData.updatedAt;
      if (currentUpdatedAt.value !== null && newUpdatedAt !== currentUpdatedAt.value) {
        // Session was updated externally, refresh pins
        try {
          const pinsData = await fetchPins(props.sessionId);
          panel.hydratePins(pinsData);
          currentUpdatedAt.value = newUpdatedAt;
        } catch {
          // Ignore errors during refresh
        }
      } else if (currentUpdatedAt.value === null) {
        // First successful poll, set the value
        currentUpdatedAt.value = newUpdatedAt;
      }
    } catch {
      // Ignore polling errors
    }
  }, POLLING_INTERVAL_MS);
}

onMounted(async () => {
  showBootstrapModal();
  await hydrateExistingSession();
  setupSessionPolling();
  trackTransfersPinningModeEntered({
    artworkId: props.artworkId,
    sessionId: props.sessionId,
    hasExistingPins: shouldHydrateExistingSession.value,
    initialMessage: pendingInitialMessage.value
  });
});

onBeforeUnmount(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
  store.pendingInitialMessage = null;
  feedback.cleanup();
  toast.clearAll();
  $(modalRef.value).modal("hide");
});

function dismissTooltip() {
  if (tooltipMode.value === "add-next") {
    tooltipDismissed.value = true;
    localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
  }
  tooltipMode.value = null;
}

function clearPinningSessionFromStore() {
  store.updateArtworkValuesById({
    id: props.artworkId,
    pinningSessionId: null,
    pinningSummary: null
  });
}

function handleStayOnCancelModal() {
  if (cancelModalType.value === "editSession") {
    trackPinsSessionEditCancelModalStayed({
      sessionId: props.sessionId,
      newPinsCount: newPinIds.value.length
    });
  } else if (cancelModalType.value === "newSession") {
    trackPinsSessionCancellationAborted({
      artworkId: props.artworkId,
      sessionId: props.sessionId,
      pinsCount: panel.pins.value.length
    });
  }
  cancelModalType.value = null;
}

function handleAddPin(coordinates) {
  if (panel.canRepositionActivePin.value) {
    panel.repositionActivePin(coordinates);
    return;
  }

  if (tooltipMode.value === "add-next" && !tooltipDismissed.value) {
    tooltipDismissed.value = true;
    localStorage.setItem(PINNING_TOOLTIP_DISMISSED_KEY, "true");
  }

  panel.addPin(coordinates);
}

function handlePinClick(pinId) {
  const currentActive = panel.activePin.value;
  if (currentActive && currentActive.state !== "ready" && currentActive.state !== "unsupported") {
    return;
  }
  if (pinId !== panel.activePinId.value) {
    const pin = panel.pins.value.find(p => p.id === pinId);
    if (pin) {
      panel.trackPinSelected(pinId, panel.activePinId.value, pin.state);
    }
  }
  panel.activePinId.value = pinId === panel.activePinId.value ? null : pinId;
}

/* eslint-disable-next-line max-lines-per-function */
function handleCancel() {
  if (!props.artworkId || !props.sessionId) {
    hideBootstrapModal();
    return;
  }

  // Editing an existing session with new pins: confirm removing new pins
  if (panel.hasExistingPin.value && hasNewPins.value) {
    cancelModalType.value = "editSession";
    return;
  }

  // Editing an existing session with no new pins: just exit without API call
  if (panel.hasExistingPin.value && !hasNewPins.value) {
    trackPinsSessionCancelled({
      artworkId: props.artworkId,
      sessionId: props.sessionId,
      pinsCount: panel.pins.value.length,
      readyPinsCount: panel.readyPinsCount.value
    });
    hideBootstrapModal();
    return;
  }

  // New session with pins: show confirmation warning
  if (panel.pins.value.length > 0) {
    cancelModalType.value = "newSession";
    return;
  }

  // New session without pins: cancel immediately
  isCancelling.value = true;
  cancelSession(props.sessionId)
    .then(() => {
      trackPinsSessionCancelled({
        artworkId: props.artworkId,
        sessionId: props.sessionId,
        pinsCount: panel.pins.value.length,
        readyPinsCount: panel.readyPinsCount.value
      });
      hideBootstrapModal();
    })
    .catch(() => {
      toast.error({
        title: "Failed to cancel session",
        description: "Something went wrong. Please try again."
      });
      hideBootstrapModal();
    })
    .finally(() => {
      clearPinningSessionFromStore();
      isCancelling.value = false;
    });
}

/* eslint-disable-next-line max-lines-per-function */
async function confirmCancel() {
  // For editing existing session: bulk delete only new pins
  if (cancelModalType.value === "editSession") {
    isCancelling.value = true;

    // Separate synced vs unsynced new pins
    // Unsynced pins are in draft state with no messages - they only exist locally
    const unsyncedPinIds = newPins.value
      .filter(p => p.state === "draft" && p.messages.length === 0)
      .map(p => p.id);
    const syncedPinIds = newPinIds.value.filter(id => !unsyncedPinIds.includes(id));

    // Always remove unsynced pins locally
    if (unsyncedPinIds.length > 0) {
      panel.pins.value = panel.pins.value.filter(p => !unsyncedPinIds.includes(p.id));
    }

    // If all new pins were unsynced, just exit without API call
    if (syncedPinIds.length === 0) {
      cancelModalType.value = null;
      isCancelling.value = false;
      hideBootstrapModal();
      return;
    }

    trackPinsSessionEditCancelModalConfirmed({
      sessionId: props.sessionId,
      newPinsCount: syncedPinIds.length
    });

    // Only call API for synced new pins
    try {
      await bulkDeletePins(props.sessionId, syncedPinIds);
      cancelModalType.value = null;
      hideBootstrapModal();
    } catch (err) {
      trackPinsSessionEditCancelFailed({
        sessionId: props.sessionId,
        newPinsCount: syncedPinIds.length,
        errorMessage: err?.message || "Unknown error"
      });
      toast.error({
        title: "Failed to remove new pins",
        description: "Something went wrong. Please try again."
      });
    } finally {
      isCancelling.value = false;
    }
    return;
  }

  // For new session: cancel entire session
  isCancelling.value = true;

  try {
    const submittedPinIds = panel.pins.value
      .filter(p => p.state === "ready" || p.state === "unsupported")
      .map(p => p.id);

    if (submittedPinIds.length > 0) {
      await bulkDeletePins(props.sessionId, submittedPinIds);
    }

    await cancelSession(props.sessionId);

    trackPinsSessionCancelled({
      artworkId: props.artworkId,
      sessionId: props.sessionId,
      pinsCount: panel.pins.value.length,
      readyPinsCount: panel.readyPinsCount.value
    });
  } catch {
    // Close even if cleanup fails
  } finally {
    clearPinningSessionFromStore();
    isCancelling.value = false;
    cancelModalType.value = null;
    hideBootstrapModal();
  }
}

async function handleDone() {
  if (panel.hasAiAssistanceOngoing.value) return;

  isCompleting.value = true;
  try {
    const data = await completeSession(props.sessionId);
    trackTransfersPinningSessionCompleted({
      artworkId: props.artworkId,
      sessionId: props.sessionId,
      pinsCount: panel.pins.value.length,
      readyPinsCount: panel.readyPinsCount.value,
      summary: data.summary ?? ""
    });
    store.completePinningSession({
      artworkIndex: props.artworkIndex,
      sessionId: props.sessionId,
      summary: data.summary ?? null
    });
    hideBootstrapModal();
  } catch (err) {
    trackTransfersPinningSessionCompletionFailed({
      artworkId: props.artworkId,
      sessionId: props.sessionId,
      errorMessage: err?.message || "Unknown error"
    });
    toast.error({
      title: "Failed to complete session",
      description: "Something went wrong. Please try again."
    });
  } finally {
    isCompleting.value = false;
  }
}
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";

.pinning-session-modal {
  &.modal {
    z-index: 2147483000;
    padding: 0 !important;
    overflow: hidden;
  }

  &__dialog {
    display: flex;
    flex-direction: column;
    width: calc(100% - 4rem);
    height: calc(100% - 4rem);
    max-width: 100%;
    margin: 2rem auto;
    background-color: #f9fafb;
    border-radius: 1rem;
    overflow: hidden;
    transform: none !important;
  }

  @media (max-width: 767px) {
    &__dialog {
      width: 100%;
      height: 100%;
      margin: 0;
      border-radius: 0;
    }
  }
}
</style>
